import { Link, useRouterState } from '@tanstack/react-router'
import { Bell, CalendarDays, Inbox, Plus, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useUnreadCount } from '@/routes/features/notifications/hooks'
import useLoginStore from '@/store/LoginStore'
import { cn } from '@/lib/utils'

const trimSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path)

const tabClass = (active: boolean) =>
  cn(
    'flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 text-[10.5px] font-medium',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
    active ? 'text-brand' : 'text-muted-foreground',
  )

function TabIcon({ icon: Icon, badge }: { icon: LucideIcon; badge?: number }) {
  return (
    <span className="relative">
      <Icon className="size-[22px]" strokeWidth={1.75} />
      {badge != null && badge > 0 && (
        <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold tabular-nums text-brand-foreground">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </span>
  )
}

/**
 * The phone tab bar (design 36a): Inbox, Next 7 days, Search, Notifications.
 * Everything else is in the drawer. In standalone/PWA, --safe-area-bottom
 * clears the home indicator; in Safari browser it is 0 so we sit just above
 * the toolbar (MOL-38). Steps aside while the keyboard is up.
 */
export function PhoneTabBar({ onSearch }: { onSearch: () => void }) {
  const { t } = useTranslation('navigation')
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const pathname = useRouterState({ select: (s) => trimSlash(s.location.pathname) })
  const { count: unread } = useUnreadCount()
  const base = `/${workspaceSlug}`

  const tabs: Array<{ to: string; label: string; icon: LucideIcon; badge?: number }> = [
    { to: `${base}/tasks`, label: t('inbox'), icon: Inbox },
    { to: `${base}/tasks/upcoming`, label: t('next7Days'), icon: CalendarDays },
    { to: `${base}/notifications`, label: t('notifications'), icon: Bell, badge: unread },
  ]
  const [inbox, upcoming, notifications] = tabs
  // The task page pins its comment field where the tab bar would be (design 36b).
  if (TASK_PAGE.test(pathname)) return null

  const link = (tab: (typeof tabs)[number]) => {
    const active = pathname === tab.to
    return (
      <Link key={tab.to} to={tab.to} aria-current={active ? 'page' : undefined} className={tabClass(active)}>
        <TabIcon icon={tab.icon} badge={tab.badge} />
        {tab.label}
      </Link>
    )
  }

  return (
    <nav
      aria-label={t('tabBar')}
      className="hidden shrink-0 border-t bg-chrome pb-[var(--safe-area-bottom)] phone:flex in-data-[keyboard=open]:hidden"
    >
      {inbox && link(inbox)}
      {upcoming && link(upcoming)}
      {/* Opens the search dialog inside the tap, so iOS raises the keyboard. */}
      <button type="button" onClick={onSearch} className={tabClass(false)}>
        <TabIcon icon={Search} />
        {t('search')}
      </button>
      {notifications && link(notifications)}
    </nav>
  )
}

const TASK_PAGE = /\/tasks\/(?!today$|upcoming$|someday$)[^/]+$/

/** Paths where the floating "+" would cover what you're doing. */
const NO_CREATE = [/\/settings(\/|$)/, /\/notes(\/|$)/, /\/notifications(\/|$)/, /\/pages\//, TASK_PAGE]

/** The phone "+" (design 36a): opens quick create, floating above the tab bar. */
export function PhoneCreateButton({ onCreate }: { onCreate: () => void }): ReactNode {
  const { t } = useTranslation('navigation')
  const pathname = useRouterState({ select: (s) => trimSlash(s.location.pathname) })
  if (NO_CREATE.some((re) => re.test(pathname))) return null
  return (
    <button
      type="button"
      onClick={onCreate}
      aria-label={t('newTask')}
      className={cn(
        'absolute bottom-[calc(var(--safe-area-bottom)+1rem)] right-4 z-30 hidden size-14 items-center justify-center rounded-2xl bg-foreground text-background shadow-lg',
        'phone:flex in-data-[keyboard=open]:hidden active:scale-95 transition-transform',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <Plus className="size-6" />
    </button>
  )
}
